import { getFormattedDate } from '../../../../../utils/dateUtils';
import {
    GstReturnSummary,
    GstReturnSummaryAvailableEntry,
    GstReturnSummaryRow,
    GstSummaryView,
} from '../../../../../store/dashboard/revenueVendorExpenses/types';
import {
    DATA_AVAILABLE_ROW_LABEL,
    DEFAULT_RETURNS_BY_VIEW,
    GST_RETURN_CODES,
    SUMMARY_CLASSIFICATION_HEADER_TYPE,
    SUMMARY_PERIOD_HEADER_TYPE,
    SUMMARY_ROW_TYPES,
    SUMMARY_TOTAL_AMOUNTS_KEY,
    SUMMARY_TOTAL_HEADER_TYPE,
} from './constants';
import { GstSummaryModel, SummaryCellValue, SummaryGstAvailability, SummaryRow } from './types';

const MONTH_YEAR_KEY = /^(\d{2})-(\d{4})$/;

const normalizeReturnCode = (code: string): string => code.replace(/[^a-z0-9]/gi, '').toUpperCase();

export const formatMonthKey = (monthKey: string): string => {
    const match = MONTH_YEAR_KEY.exec(monthKey);
    return match ? getFormattedDate(`${match[2]}-${match[1]}-01`, 'Monthly') : monthKey;
};

const toCellValue = (value: unknown): SummaryCellValue => {
    if (typeof value === 'string' && value.trim() !== '') return toCellValue(Number(value));
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const normalizeNotes = (row: GstReturnSummaryRow, returns: string[]): Record<string, string> => {
    const notes: Record<string, string> = {};
    const { note, notes: rawNotes } = row;
    if (rawNotes && typeof rawNotes === 'object') {
        Object.entries(rawNotes).forEach(([code, text]) => {
            if (text) notes[normalizeReturnCode(code)] = text;
        });
    }
    const plainNote = typeof rawNotes === 'string' ? rawNotes : note;
    if (plainNote) {
        // A plain note belongs to the 3B figure (the e-commerce footnote); other views fall back to the first return.
        const target = returns.find((code) => code === GST_RETURN_CODES.GSTR3B) ?? returns[0];
        if (target && !notes[target]) notes[target] = plainNote;
    }
    return notes;
};

const normalizeRow = (row: GstReturnSummaryRow, index: number, returns: string[]): SummaryRow => {
    const amounts: SummaryRow['amounts'] = {};
    Object.entries(row.amounts || {}).forEach(([period, perReturn]) => {
        const normalized: Record<string, SummaryCellValue> = {};
        Object.entries(perReturn || {}).forEach(([code, value]) => {
            normalized[normalizeReturnCode(code)] = toCellValue(value);
        });
        const periodKey = period.toLowerCase() === SUMMARY_TOTAL_AMOUNTS_KEY ? SUMMARY_TOTAL_AMOUNTS_KEY : period;
        amounts[periodKey] = normalized;
    });
    const label = row.label ?? '';
    return {
        key: row.key ?? `${label}-${index}`,
        label,
        isCreditNote: row.rowType === SUMMARY_ROW_TYPES.CREDIT_NOTE,
        isBold: row.rowType === SUMMARY_ROW_TYPES.TOTAL,
        amounts,
        notes: normalizeNotes(row, returns),
    };
};

const isDataAvailableRow = (row: GstReturnSummaryRow) =>
    /data[_\s]?available/i.test(row.rowType ?? '') || row.label === DATA_AVAILABLE_ROW_LABEL;

const toAvailability = (entry: GstReturnSummaryAvailableEntry): SummaryGstAvailability => ({
    gst: entry.gst,
    state: entry.state ?? null,
    dataSource: entry.dataSource,
});

const normalizeAvailability = (gstAvailable: GstReturnSummary['gstAvailable']): GstSummaryModel['availability'] => {
    const availability: GstSummaryModel['availability'] = {};
    Object.entries(gstAvailable || {}).forEach(([code, value]) => {
        const perMonth: Record<string, SummaryGstAvailability[]> = {};
        if (Array.isArray(value)) {
            value.forEach((entry) => {
                const monthKey = entry.date;
                (perMonth[monthKey] = perMonth[monthKey] || []).push(toAvailability(entry));
            });
        } else {
            // Month-keyed shape ({ 'MM-YYYY': { gsts: [...] } }), the layout the GSTR-3B route used.
            Object.entries(value || {}).forEach(([month, monthEntry]) => {
                perMonth[month] = (monthEntry?.gsts || []).map(toAvailability);
            });
        }
        availability[normalizeReturnCode(code)] = perMonth;
    });
    return availability;
};

/**
 * The single place that knows the getGstReturnSummary response layout. A null/absent summary yields an
 * empty model (no months), which the table renders as the empty state.
 */
export function parseGstReturnSummary(summary: GstReturnSummary | null | undefined, view: GstSummaryView): GstSummaryModel {
    const headers = summary?.headers || [];
    const returns = summary?.returns?.length ? summary.returns.map(normalizeReturnCode) : DEFAULT_RETURNS_BY_VIEW[view];
    return {
        view,
        classificationLabel: headers.find((header) => header.type === SUMMARY_CLASSIFICATION_HEADER_TYPE)?.value ?? '',
        returns,
        months: headers
            .filter((header) => header.type === SUMMARY_PERIOD_HEADER_TYPE)
            .map((header) => ({ key: header.value, label: formatMonthKey(header.value) })),
        hasTotal: headers.some((header) => header.type === SUMMARY_TOTAL_HEADER_TYPE),
        rows: (summary?.rows || []).filter((row) => !isDataAvailableRow(row)).map((row, i) => normalizeRow(row, i, returns)),
        availability: normalizeAvailability(summary?.gstAvailable),
    };
}

export const getSummaryCell = (row: SummaryRow, periodKey: string, returnCode: string): SummaryCellValue =>
    row.amounts[periodKey]?.[returnCode] ?? null;

/** A row belongs to a return when that return appears in any period or the total, even as null ("Not available"). */
export const rowHasReturn = (row: SummaryRow, returnCode: string): boolean =>
    Object.values(row.amounts).some((perReturn) => returnCode in perReturn);
